import React, { createContext, useContext, useEffect, useState } from "react"

type Theme = `light` | `dark` | `system`

interface ThemeContextType {
  theme: Theme
  setTheme: (theme: Theme) => void
  isDark: boolean
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(`system`)
  const [isDark, setIsDark] = useState(false)
  const [mounted, setMounted] = useState(false)

  // Load theme from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(`theme`) as Theme | null
    if (stored) {
      setThemeState(stored)
    }
    setMounted(true)
  }, [])

  // Apply theme when it changes
  useEffect(() => {
    if (!mounted) return

    let effectiveTheme: `light` | `dark` =
      theme === `system`
        ? window.matchMedia(`(prefers-color-scheme: dark)`).matches
          ? `dark`
          : `light`
        : theme

    setIsDark(effectiveTheme === `dark`)

    if (effectiveTheme === `dark`) {
      document.documentElement.classList.add(`dark`)
    } else {
      document.documentElement.classList.remove(`dark`)
    }

    localStorage.setItem(`theme`, theme)
  }, [theme, mounted])

  // Listen for system theme changes
  useEffect(() => {
    if (theme !== `system`) return

    const mediaQuery = window.matchMedia(`(prefers-color-scheme: dark)`)
    const handleChange = (e: MediaQueryListEvent) => {
      setIsDark(e.matches)
      if (e.matches) {
        document.documentElement.classList.add(`dark`)
      } else {
        document.documentElement.classList.remove(`dark`)
      }
    }

    mediaQuery.addEventListener(`change`, handleChange)
    return () => mediaQuery.removeEventListener(`change`, handleChange)
  }, [theme])

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme)
  }

  if (!mounted) {
    return <>{children}</>
  }

  return (
    <ThemeContext.Provider value={{ theme, setTheme, isDark }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    // Return default values if ThemeProvider hasn't mounted yet
    return {
      theme: `system` as Theme,
      setTheme: () => {},
      isDark: false,
    }
  }
  return context
}
